import React, { useEffect, useRef, useState } from 'react';
import cl from './StudioAnim.module.css';
import Diagram from './Diagram';
import Part from './Part';

/**
 * StudioAnim — движок анимаций «как в Студии».
 *
 * Заменяет статичные рисунки: каждый кадр теории показывает СХЕМУ реального
 * интерфейса Рублокс Студии, по которой ездит курсор, нажимаются кнопки и
 * на сцене появляются объекты. Подход взят из демо Кубоши (ComicMovie:
 * DemoKubosha) — там слева программа, справа сцена, активный блок
 * подсвечивается, и это единственная часть теории, которая выглядела живой.
 *
 * Отличия от комикса, от которого мы отказались:
 *   • цвет вместо чёрно-белого карандаша;
 *   • макет настоящего редактора вместо абстрактных образов;
 *   • НИКАКИХ декоративных фонов — на кадре только то, что объясняется;
 *   • анимация на каждом кадре, а не подпись под картинкой.
 *
 * Палитра совпадает с редактором (KubikonEditor.module.css), чтобы ученик
 * узнавал интерфейс, а не запоминал две разные картинки.
 *
 * СЦЕНАРИЙ — список шагов, исполняются по очереди:
 *   { act: 'move',   to: 'tool-primitive', dur: 700 }   курсор едет к цели
 *   { act: 'click',  dur: 300 }                          щелчок в текущей точке
 *   { act: 'panel',  open: 'primitives' }                открыть левую панель
 *   { act: 'spawn',  obj: {type,x,y,label}, dur: 500 }   объект появляется на сцене
 *   { act: 'select', id: 'cube1' }                       выделить объект
 *   { act: 'move3d', id: 'cube1', y: -40, dur: 600 }     подвинуть объект в сцене
 *   { act: 'prop',   name: 'Y', value: '3' }             изменить свойство в инспекторе
 *   { act: 'orbit',  dur: 900 }                          облёт камеры вокруг сцены
 *   { act: 'axes',   show: true }                        показать оси X/Y/Z
 *   { act: 'note',   text: '...', at: 'scene' }          выноска-пояснение
 *   { act: 'wait',   dur: 400 }                          пауза
 *
 * Props:
 *   spec    — { steps: [...], layout?: 'full'|'scene'|'inspector' }
 *   active  — проигрывать (кадр в фокусе)
 */

/*
 * Точки интерфейса «Кубикон CAD», к которым может ехать курсор
 * (проценты внутри макета).
 *
 * Соответствуют реальному TopBar редактора: строка рабочих областей
 * (ТЕЛО · ПОВЕРХНОСТЬ · ЛИСТ · ИНСТРУМЕНТЫ) и лента групп
 * ЭСКИЗ · СОЗДАТЬ · ИЗМЕНИТЬ. Если в редакторе появится новая кнопка,
 * добавлять её надо и сюда — иначе курсор поедет в пустоту.
 */
const TARGETS = {
    // строка рабочих областей
    'tab-solid': { x: 12, y: 4 },
    'tab-surface': { x: 23, y: 4 },
    'tab-sheet': { x: 34, y: 4 },
    'tab-tools': { x: 45, y: 4 },
    // лента: ЭСКИЗ
    'tool-sketch': { x: 6, y: 15 },
    // лента: СОЗДАТЬ
    'tool-extrude': { x: 18, y: 15 },
    'tool-revolve': { x: 27, y: 15 },
    'tool-hole': { x: 36, y: 15 },
    // лента: ИЗМЕНИТЬ
    'tool-fillet': { x: 49, y: 15 },
    'tool-chamfer': { x: 58, y: 15 },
    'tool-shell': { x: 67, y: 15 },
    // инструменты эскиза (появляются в режиме эскиза)
    'sk-line': { x: 6, y: 15 },
    'sk-rect': { x: 15, y: 15 },
    'sk-circle': { x: 24, y: 15 },
    'sk-dim': { x: 33, y: 15 },
    'sk-text': { x: 42, y: 15 },
    'sk-finish': { x: 88, y: 15 },
    // сцена и панели
    'scene': { x: 45, y: 58 },
    'plane-xy': { x: 45, y: 60 },
    'plane-xz': { x: 38, y: 52 },
    'plane-yz': { x: 52, y: 52 },
    'browser': { x: 8, y: 55 },
    'props': { x: 88, y: 45 },
    'timeline': { x: 45, y: 92 },
    'viewcube': { x: 78, y: 30 },
};

const START_CURSOR = { x: 50, y: 90 };

export default function StudioAnim({ spec, active, still }) {
    const [cursor, setCursor] = useState(START_CURSOR);
    const [clicking, setClicking] = useState(false);
    const [panel, setPanel] = useState(null);
    const [objects, setObjects] = useState([]);
    const [selected, setSelected] = useState(null);
    const [props, setProps] = useState([]);
    const [note, setNote] = useState(null);
    const [orbit, setOrbit] = useState(0);
    const [axes, setAxes] = useState(false);
    const [pressed, setPressed] = useState(null);
    const [pointed, setPointed] = useState(null);   // подсвеченная цель до клика

    const rafRef = useRef(0);
    const timersRef = useRef([]);
    /* Корень макета — от него считаем позиции кнопок. */
    const rootRef = useRef(null);

    /*
     * Где на самом деле находится цель, в процентах кадра.
     *
     * ⚠️ Меряем DOM, а не берём число из TARGETS: лента верстается флексом,
     * и позиции кнопок зависят от ширины кадра, длины подписей и режима
     * (в эскизе лента другая). Рукописные проценты с этим всегда расходятся —
     * курсор промахивался мимо кнопки, которую якобы нажимает.
     */
    const measure = (name) => {
        const root = rootRef.current;
        if (root && name) {
            const el = root.querySelector(`[data-btn="${name}"]`)
                || root.querySelector(`[data-zone="${name}"]`);
            if (el) {
                const r = el.getBoundingClientRect();
                const b = root.getBoundingClientRect();
                if (b.width > 0 && b.height > 0) {
                    return {
                        x: ((r.left + r.width / 2 - b.left) / b.width) * 100,
                        y: ((r.top + r.height / 2 - b.top) / b.height) * 100,
                    };
                }
            }
        }
        /* Запасной путь: цель без своего элемента (точка в пустой сцене). */
        return TARGETS[name] || null;
    };

    // Сброс и проигрывание сценария при активации кадра.
    useEffect(() => {
        const reset = () => {
            setCursor(START_CURSOR); setClicking(false); setPanel(null);
            setObjects([]); setSelected(null); setProps([]); setNote(null);
            setOrbit(0); setAxes(false); setPressed(null); setPointed(null);
        };

        if (!active) { reset(); return undefined; }

        let cancelled = false;
        reset();

        const timers = timersRef.current = [];
        const wait = (ms) => new Promise((res) => {
            timers.push(setTimeout(res, ms));
        });

        // Плавная интерполяция значения через requestAnimationFrame.
        const anim = (dur, fn) => new Promise((res) => {
            const t0 = performance.now();
            const frame = (now) => {
                if (cancelled) return res();
                const k = Math.min(1, (now - t0) / dur);
                fn(ease(k));
                if (k < 1) rafRef.current = requestAnimationFrame(frame);
                else res();
            };
            rafRef.current = requestAnimationFrame(frame);
        });

        (async () => {
            await wait(260);
            let cur = { ...START_CURSOR };

            for (const step of (spec?.steps || [])) {
                if (cancelled) return;

                switch (step.act) {
                    case 'move': {
                        const to = measure(step.to) || step.pos || cur;
                        const from = { ...cur };
                        await anim(step.dur || 700, (e) => {
                            setCursor({
                                x: from.x + (to.x - from.x) * e,
                                y: from.y + (to.y - from.y) * e,
                            });
                        });
                        cur = { ...to };
                        break;
                    }

                    case 'click': {
                        setClicking(true);
                        setPointed(null);
                        if (step.target) setPressed(step.target);
                        await wait(step.dur || 280);
                        setClicking(false);
                        await wait(120);
                        setPressed(null);
                        break;
                    }

                    case 'panel':
                        setPanel(step.open || null);
                        await wait(step.dur || 350);
                        break;

                    case 'spawn': {
                        const obj = { id: step.id || `o${Date.now()}`, ...step.obj, appear: 0 };
                        setObjects((list) => [...list, obj]);
                        await anim(step.dur || 480, (e) => {
                            setObjects((list) => list.map((o) =>
                                o.id === obj.id ? { ...o, appear: e } : o));
                        });
                        break;
                    }

                    case 'select':
                        setSelected(step.id);
                        await wait(step.dur || 300);
                        break;

                    case 'move3d': {
                        const start = {};
                        setObjects((list) => {
                            const o = list.find((x) => x.id === step.id);
                            if (o) { start.x = o.x; start.y = o.y; }
                            return list;
                        });
                        await wait(20);
                        await anim(step.dur || 600, (e) => {
                            setObjects((list) => list.map((o) => o.id !== step.id ? o : {
                                ...o,
                                x: (start.x ?? o.x) + (step.dx || 0) * e,
                                y: (start.y ?? o.y) + (step.dy || 0) * e,
                            }));
                        });
                        break;
                    }

                    case 'prop':
                        setProps((list) => {
                            const i = list.findIndex((p) => p.name === step.name);
                            const item = { name: step.name, value: step.value, hot: true };
                            if (i >= 0) { const c = [...list]; c[i] = item; return c; }
                            return [...list, item];
                        });
                        await wait(step.dur || 420);
                        setProps((list) => list.map((p) => ({ ...p, hot: false })));
                        break;

                    case 'orbit':
                        await anim(step.dur || 900, (e) => setOrbit((step.deg || 30) * e));
                        break;

                    case 'axes':
                        setAxes(step.show !== false);
                        await wait(step.dur || 300);
                        break;

                    /* 'play' (запуск игры) остался от Рублокс Студии — в CAD
                       запускать нечего, деталь не «играет». Шаг игнорируем,
                       чтобы старый сценарий не падал. */
                    case 'play':
                        await wait(step.dur || 500);
                        break;

                    case 'point':
                        // Подсветка цели ДО клика — направить внимание.
                        // Рисуется В КАДРЕ, а не поверх живого интерфейса:
                        // подсветка живого DOM ломается при каждом изменении UI
                        // (так забросили Godot-плагин, Unreal и мучается Unity).
                        setPressed(null);
                        setPointed(step.target);
                        await wait(step.dur || 700);
                        break;

                    case 'note':
                        setNote({ text: step.text, at: step.at || 'scene' });
                        await wait(step.dur || 900);
                        if (step.keep !== true) setNote(null);
                        break;

                    case 'wait':
                    default:
                        await wait(step.dur || 300);
                }
            }
        })();

        return () => {
            cancelled = true;
            cancelAnimationFrame(rafRef.current);
            timers.forEach(clearTimeout);
        };
    }, [active, spec]);

    // Кадр-объяснение: СТАТИЧНАЯ схема вместо анимации интерфейса.
    // Анимация помогает для действий (d≈1.06) и почти не помогает для
    // концепций (d≈0.37), зато добавляет транзиторность — информация
    // исчезает с экрана и грузит память. Схему можно рассматривать.
    if (still || spec?.diagram) {
        return <Diagram kind={spec.diagram} labels={spec.labels} />;
    }

    const layout = spec?.layout || 'full';

    return (
        <div ref={rootRef} className={`${cl.wrap} ${cl['l_' + layout]}`}>
            {/*
              * ── Верхняя лента «Кубикон CAD» ──────────────────────
              *
              * Повторяет реальный TopBar: строка рабочих областей и лента
              * групп с разделителями. В режиме эскиза лента подменяется на
              * инструменты рисования — ровно как в редакторе, иначе ученик
              * не поймёт, куда делись кнопки.
              */}
            {layout !== 'scene' && (
                <div className={cl.ribbon}>
                    <div className={cl.tabs}>
                        <span className={cl.fileBtn}>Файл</span>
                        {['ТЕЛО', 'ПОВЕРХНОСТЬ', 'ЛИСТ', 'ИНСТРУМЕНТЫ'].map((t, i) => (
                            <span key={t} className={`${cl.tab} ${i === 0 ? cl.tabOn : ''}`}>{t}</span>
                        ))}
                    </div>

                    {panel === 'sketch-mode' ? (
                        <div className={cl.tools}>
                            <Group label="СОЗДАТЬ">
                                <Btn label="Линия" icon="line" id="sk-line"
                                    on={pressed === 'sk-line'} aim={pointed === 'sk-line'} />
                                <Btn label="Прямоугольник" icon="rect" id="sk-rect"
                                    on={pressed === 'sk-rect'} aim={pointed === 'sk-rect'} />
                                <Btn label="Окружность" icon="circle" id="sk-circle"
                                    on={pressed === 'sk-circle'} aim={pointed === 'sk-circle'} />
                            </Group>
                            <Group label="РАЗМЕРЫ">
                                <Btn label="Размер" icon="dim" id="sk-dim"
                                    on={pressed === 'sk-dim'} aim={pointed === 'sk-dim'} />
                                <Btn label="Текст" icon="text" id="sk-text"
                                    on={pressed === 'sk-text'} aim={pointed === 'sk-text'} />
                            </Group>
                            <span className={cl.spacer} />
                            <span data-btn="sk-finish"
                                className={`${cl.finishBtn} ${pressed === 'sk-finish' ? cl.finishOn : ''} ${pointed === 'sk-finish' ? cl.finishAim : ''}`}>
                                Закрыть эскиз
                            </span>
                        </div>
                    ) : (
                        <div className={cl.tools}>
                            <Group label="ЭСКИЗ">
                                <Btn label="Эскиз" icon="sketch" id="tool-sketch"
                                    on={pressed === 'tool-sketch'} aim={pointed === 'tool-sketch'} />
                            </Group>
                            <Group label="СОЗДАТЬ">
                                <Btn label="Выдавить" icon="extrude" id="tool-extrude"
                                    on={pressed === 'tool-extrude'} aim={pointed === 'tool-extrude'} />
                                <Btn label="Вращение" icon="revolve" id="tool-revolve"
                                    on={pressed === 'tool-revolve'} aim={pointed === 'tool-revolve'} />
                                <Btn label="Отверстие" icon="hole" id="tool-hole"
                                    on={pressed === 'tool-hole'} aim={pointed === 'tool-hole'} />
                            </Group>
                            <Group label="ИЗМЕНИТЬ">
                                <Btn label="Скругление" icon="fillet" id="tool-fillet"
                                    on={pressed === 'tool-fillet'} aim={pointed === 'tool-fillet'} />
                                <Btn label="Фаска" icon="chamfer" id="tool-chamfer"
                                    on={pressed === 'tool-chamfer'} aim={pointed === 'tool-chamfer'} />
                                <Btn label="Оболочка" icon="shell" id="tool-shell"
                                    on={pressed === 'tool-shell'} aim={pointed === 'tool-shell'} />
                            </Group>
                        </div>
                    )}
                </div>
            )}

            <div className={cl.body}>
                {/*
                  * ── Левая панель ───────────────────────────────
                  * В CAD слева стоит браузер документа; панель выбора
                  * появляется только когда операция этого требует —
                  * например, выбор плоскости для эскиза.
                  */}
                {layout === 'full' && (
                    <div data-zone="browser"
                        className={`${cl.left} ${panel ? cl.leftOpen : ''}`}>
                        {panel === 'planes' && (
                            <>
                                <div className={cl.panelTitle}>Выбери плоскость</div>
                                <div className={cl.grid}>
                                    {['XY — сверху', 'XZ — спереди', 'YZ — сбоку'].map((n, i) => (
                                        <div key={n}
                                            className={`${cl.cell} ${i === 0 && pressed === 'pick' ? cl.cellOn : ''}`}>
                                            <span className={cl.planeIco} data-plane={i} />
                                            <span className={cl.cellTxt}>{n}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/*
                  * ── Сцена CAD ──────────────────────────────────
                  *
                  * Вьюпорт СВЕТЛЫЙ, с миллиметровой сеткой — как в редакторе.
                  * Никакого неба и травы: в CAD ученик видит белое поле,
                  * и мультик должен совпадать с тем, что он откроет.
                  * Вся сцена поворачивается по orbit — облёт камеры честный:
                  * вращается плоскость земли вместе с деталью.
                  */}
                <div data-zone="scene" className={cl.scene}>
                    <div className={cl.grid3d}
                        style={{ transform: `perspective(680px) rotateX(56deg) rotateZ(${orbit}deg)` }}>
                        <div className={cl.gridLines} />
                    </div>

                    {axes && (
                        <svg className={cl.axes} viewBox="0 0 200 140">
                            <defs>
                                <marker id="kc-ah" markerWidth="6" markerHeight="6"
                                    refX="5" refY="3" orient="auto">
                                    <path d="M0 0 L6 3 L0 6 Z" fill="currentColor" />
                                </marker>
                            </defs>
                            {/* Цвета осей — из редактора: X красная, Y зелёная, Z синяя. */}
                            <g strokeWidth="2.2" markerEnd="url(#kc-ah)" fill="none">
                                <path d="M100 96 L100 36" stroke="#4a7ec0" color="#4a7ec0" />
                                <path d="M100 96 L166 120" stroke="#c0504d" color="#c0504d" />
                                <path d="M100 96 L34 120" stroke="#4f9a4f" color="#4f9a4f" />
                            </g>
                            <text x="100" y="28" className={cl.axLabel} fill="#4a7ec0">Z</text>
                            <text x="174" y="128" className={cl.axLabel} fill="#c0504d">X</text>
                            <text x="26" y="128" className={cl.axLabel} fill="#4f9a4f">Y</text>
                        </svg>
                    )}

                    {/*
                      * Детали рисуются одним SVG поверх сцены. Каждая — Part
                      * со своими габаритами и признаками (отверстия, паз,
                      * скругления, оболочка): ученик должен видеть ИМЕННО ту
                      * деталь, о которой урок, а не универсальный кубик.
                      */}
                    {objects.length > 0 && (
                        <svg className={cl.partLayer} viewBox="-160 -120 320 240"
                            preserveAspectRatio="xMidYMid meet">
                            {objects.map((o) => (
                                <g key={o.id}
                                    transform={`translate(${o.x || 0} ${(o.y || 0) - 10})`}>
                                    <Part {...o} appear={o.appear ?? 1}
                                        selected={selected === o.id}/>
                                </g>
                            ))}
                        </svg>
                    )}
                    {/* Подписи деталей — отдельным слоем, чтобы не масштабировались
                        вместе с SVG и оставались читаемыми. */}
                    {objects.filter((o) => o.label).map((o) => (
                        <span key={`t_${o.id}`} className={cl.partTag}
                            style={{
                                left: `calc(50% + ${o.x || 0}px)`,
                                top: `calc(50% + ${(o.y || 0) - 58}px)`,
                                opacity: o.appear ?? 1,
                            }}>{o.label}</span>
                    ))}

                    {/* Куб-навигатор в углу — он есть в редакторе, и уроки
                        про виды на него ссылаются. */}
                    <div data-zone="viewcube" className={cl.viewcube}
                        style={{ transform: `perspective(220px) rotateX(-24deg) rotateY(${-orbit - 32}deg)` }}>
                        <i className={cl.vcTop} />
                        <i className={cl.vcFront}>СПЕРЕДИ</i>
                        <i className={cl.vcRight} />
                    </div>

                    {note?.at === 'scene' && <div className={cl.note}>{note.text}</div>}
                </div>

                {/*
                  * ── Правая колонка ─────────────────────────────
                  * В редакторе это «Браузер» (дерево документа) и панель
                  * свойств операции. Названия те же, что видит ученик.
                  */}
                {layout !== 'scene' && (
                    <div data-zone="props" className={cl.right}>
                        <div className={cl.panelTitle}>Браузер</div>
                        <div className={cl.tree}>
                            <div className={cl.treeRow}>
                                <span className={cl.treeDot} style={{ background: '#8a95a6' }} />
                                Начало
                            </div>
                            {objects.length === 0 && (
                                <div className={cl.treeEmpty}>тел пока нет</div>
                            )}
                            {objects.map((o) => (
                                <div key={o.id}
                                    className={`${cl.treeRow} ${selected === o.id ? cl.treeOn : ''}`}>
                                    <span className={cl.treeDot}
                                        style={{ background: o.color || '#8f9aab' }} />
                                    {o.label || o.type}
                                </div>
                            ))}
                        </div>

                        <div className={cl.panelTitle}>Свойства</div>
                        <div className={cl.propList}>
                            {props.length === 0 && <div className={cl.treeEmpty}>—</div>}
                            {props.map((p) => (
                                <div key={p.name} className={`${cl.propRow} ${p.hot ? cl.propHot : ''}`}>
                                    <span className={cl.propName}>{p.name}</span>
                                    <span className={cl.propVal}>{p.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ── Курсор ────────────────────────────────────────── */}
            <div
                className={`${cl.cursor} ${clicking ? cl.cursorClick : ''}`}
                style={{ left: cursor.x + '%', top: cursor.y + '%' }}
            >
                <svg viewBox="0 0 16 20" width="17" height="21">
                    <path d="M1 1 L1 15 L5 11.5 L7.6 17.6 L10.2 16.4 L7.7 10.6 L13 10.4 Z"
                          fill="#fff" stroke="#111" strokeWidth="1.2" strokeLinejoin="round" />
                </svg>
                {clicking && <span className={cl.ripple} />}
            </div>
        </div>
    );
}

/*
 * Иконки инструментов — те же линии, что на кнопках редактора
 * (скопированы из ui/icons.jsx). Ученик должен узнавать кнопку по значку,
 * а не по подписи: цветной квадратик вместо иконки этого не даёт.
 */
const ICONS = {
    sketch: <><rect x="5" y="6" width="18" height="20" rx="1.5"/><path d="M14 14 L26 26"/>
        <path d="M9 11 L17 11"/><path d="M9 15 L13 15"/></>,
    extrude: <><rect x="6" y="20" width="14" height="6" strokeDasharray="2 2" opacity="0.55"/>
        <path d="M6 20 L9 14 L23 14 L20 20"/><path d="M9 14 L9 8"/><path d="M23 14 L23 8"/>
        <path d="M9 8 L23 8"/><path d="M16 6 L16 2 M13 5 L16 2 L19 5"/></>,
    revolve: <><path d="M16 3 L16 29" strokeDasharray="2 2" opacity="0.6"/>
        <path d="M10 8 Q22 16 10 24"/><ellipse cx="16" cy="16" rx="9" ry="4"/></>,
    hole: <><rect x="5" y="6" width="22" height="20" rx="1.5"/>
        <circle cx="16" cy="16" r="4"/><circle cx="16" cy="16" r="2"/></>,
    fillet: <><path d="M6 26 L6 14 Q6 6 14 6 L26 6"/>
        <path d="M6 26 L26 26 L26 6" opacity="0.45" strokeDasharray="2 2"/></>,
    chamfer: <><path d="M6 26 L6 14 L14 6 L26 6"/>
        <path d="M6 26 L26 26 L26 6" opacity="0.45" strokeDasharray="2 2"/></>,
    shell: <><path d="M5 9 L16 4 L27 9 L16 14 Z"/><path d="M5 9 L5 23 L16 28 L27 23 L27 9"/>
        <path d="M16 14 L16 28" opacity="0.5"/></>,
    line: <><path d="M5 26 L27 7"/><circle cx="5" cy="26" r="2"/><circle cx="27" cy="7" r="2"/></>,
    rect: <><rect x="5" y="8" width="22" height="16"/><circle cx="5" cy="8" r="1.8"/>
        <circle cx="27" cy="24" r="1.8"/></>,
    circle: <><circle cx="16" cy="16" r="10"/><circle cx="16" cy="16" r="1.6"/>
        <path d="M16 16 L26 16" strokeDasharray="2 2"/></>,
    dim: <><path d="M5 10 L5 22"/><path d="M27 10 L27 22"/><path d="M5 16 L27 16"/>
        <path d="M8 13 L5 16 L8 19"/><path d="M24 13 L27 16 L24 19"/></>,
    text: <><path d="M7 9 L7 7 L25 7 L25 9"/><path d="M16 7 L16 25"/>
        <path d="M12 25 L20 25"/></>,
};

/** Кнопка ленты: настоящая иконка + подпись, как в редакторе. */
function Btn({ label, icon, on, aim, id }) {
    return (
        <span data-btn={id}
            className={`${cl.btn} ${on ? cl.btnOn : ''} ${aim ? cl.btnAim : ''}`}>
            <svg className={cl.btnIco} viewBox="0 0 32 32" fill="none"
                stroke="currentColor" strokeWidth="1.7"
                strokeLinecap="round" strokeLinejoin="round">
                {ICONS[icon]}
            </svg>
            <span className={cl.btnTxt}>{label}</span>
        </span>
    );
}

/** Группа ленты: кнопки, подпись снизу, разделитель справа — как в TopBar. */
function Group({ label, children }) {
    return (
        <span className={cl.group}>
            <span className={cl.groupBody}>{children}</span>
            <span className={cl.groupLabel}>{label}</span>
        </span>
    );
}

/** Плавность движения — как в демо Кубоши. */
function ease(k) {
    return k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
}
